/* eslint-disable eqeqeq */
import React, { Component } from "react";
import { HttpRequest } from "../../../utils/httpRequests";

const CATS = [
  { id: 1, name: "Custom Photobooks", color: "#185FA5" },
  { id: 2, name: "Themed Photobooks", color: "#534AB7" },
  { id: 3, name: "Photo Tiles", color: "#0F6E56" },
  { id: 4, name: "Retro Prints", color: "#854F0B" },
  { id: 5, name: "Photo Prints", color: "#993556" },
  { id: 6, name: "Photo Calendars", color: "#A32D2D" },
  { id: 7, name: "Photo Strips", color: "#3C3489" },
  { id: 8, name: "Photo Magnets", color: "#27500A" },
  { id: 9, name: "Photo Mugs", color: "#633806" },

];

const CAT_BY_ID = Object.fromEntries(CATS.map(c => [c.id, c]));

// ── Helpers ───────────────────────────────────────────────────────────────────
function startOf(period) {
  const d = new Date();
  if (period === "day") { d.setHours(0, 0, 0, 0); }
  if (period === "week") { d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); }
  if (period === "month") { d.setDate(1); d.setHours(0, 0, 0, 0); }
  return d;
}

// Deduplicate by orderId so total count = unique orders, not item rows
function filterByPeriod(arr = [], period) {
  const start = startOf(period);
  const filtered = arr.filter(o => new Date(o.receivedAt || o.orderDate) >= start);
  const seen = {};
  return filtered.filter(o => {
    if (seen[o.orderId]) return false;
    seen[o.orderId] = true;
    return true;
  });
}

function filterUsersByPeriod(arr = [], period) {
  const start = startOf(period);
  return arr.filter(u => new Date(u.createdAt) >= start);
}

function matchCat(categoryId) {
  return CAT_BY_ID[Number(categoryId)] || null;
}

// Count unique orders per category (not item rows)
function countByCat(orders = []) {
  const m = {};
  CATS.forEach(c => (m[c.name] = 0));
  const seenOrders = {};
  orders.forEach(o => {
    if (seenOrders[o.orderId]) return;
    seenOrders[o.orderId] = true;
    const cat = matchCat(o.categoryId);
    if (cat) m[cat.name]++;
  });
  return m;
}

function lastDateByCat(orders = []) {
  const m = {};
  const seenOrders = {};
  orders.forEach(o => {
    if (seenOrders[o.orderId]) return;
    seenOrders[o.orderId] = true;
    const cat = matchCat(o.categoryId);
    if (!cat) return;
    const d = new Date(o.receivedAt || o.orderDate);
    if (!m[cat.name] || d > m[cat.name]) m[cat.name] = d;
  });
  return m;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-PK", { day: "numeric", month: "short" });
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  card: {
    background: "#fff",
    border: "1.5px solid #bcd9f7ff",
    borderRadius: 14,
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  // Week card stretches to fill full height of the right column
  cardWeek: {
    background: "#fff",
    border: "1.5px solid #bcd9f7ff",
    borderRadius: 14,
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    height: "100%",
    minWidth: 0,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  periodLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.9,
    textTransform: "uppercase",
    color: "#9CA3AF",
  },
  divider: { height: 0.5, background: "#F3F4F6", width: "100%", flexShrink: 0 },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    color: "#9CA3AF",
  },
  userBox: {
    background: "#F9FAFB",
    borderRadius: 10,
    padding: "11px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
};

function Badge({ bg, color, children }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      background: bg,
      color: color,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}>
      {children}
    </span>
  );
}

// ── Category tile grid (Day & Month) ─────────────────────────────────────────
function CatGrid({ counts }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", width: "100%" }}>
      {CATS.map((c, i) => {
        const totalRows = Math.ceil(CATS.length / 3);
        const currentRow = Math.floor(i / 3);
        const isLastRow = currentRow === totalRows - 1;
        const isLastCol = (i + 1) % 3 === 0;
        return (
          <div
            key={c.name}
            style={{
              padding: "7px 8px",
              borderBottom: isLastRow ? "none" : "0.5px solid #F3F4F6",
              borderRight: isLastCol ? "none" : "0.5px solid #F3F4F6",
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {c.name}
              </span>
            </div>
            <span style={{ fontSize: 17, fontWeight: 500, color: c.color }}>{counts[c.name] || 0}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Category list for Week ────────────────────────────────────────────────────
function CatListWeek({ counts, lastDates }) {
  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", paddingBottom: 6, borderBottom: "0.5px solid #E5E7EB", marginBottom: 2 }}>
        <span style={{ ...S.sectionLabel, flex: 1, minWidth: 0 }}>Category</span>
        <span style={{ ...S.sectionLabel, width: 50, textAlign: "center", flexShrink: 0 }}>Orders</span>
        <span style={{ ...S.sectionLabel, width: 64, textAlign: "right", flexShrink: 0 }}>Last order</span>
      </div>
      {CATS.map(c => (
        <div key={c.name} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid #F3F4F6" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0, overflow: "hidden" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {c.name}
            </span>
          </div>
          <span style={{ width: 50, fontSize: 18, fontWeight: 500, color: c.color, textAlign: "center", flexShrink: 0 }}>
            {counts[c.name] || 0}
          </span>
          <span style={{ width: 64, fontSize: 10, color: "#9CA3AF", textAlign: "right", flexShrink: 0 }}>
            {fmtDate(lastDates[c.name])}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── TODAY card ────────────────────────────────────────────────────────────────
function DayCard({ orders, userCount }) {
  const now = new Date();
  const filtered = filterByPeriod(orders, "day");
  return (
    <div style={S.card}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={S.periodLabel}>Today</div>
          <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>
            {now.toLocaleDateString("en-PK", { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 36, fontWeight: 500, color: "#111827", lineHeight: 1 }}>{filtered.length}</span>
          <span style={{ fontSize: 16, color: "#9CA3AF" }}>orders</span>
        </div>
      </div>



      <div style={S.divider} />

      <div>
        <p style={{ ...S.sectionLabel, marginBottom: 8 }}>By category</p>
        <CatGrid counts={countByCat(filtered)} />
      </div>

      <div style={S.divider} />

      <div style={S.userBox}>
        <span style={{ fontSize: 11, color: "#6B7280" }}>New registrations</span>
        <span style={{ fontSize: 18, fontWeight: 500, color: "#27500A" }}>{userCount}</span>
      </div>
    </div>
  );
}

// ── THIS MONTH card ───────────────────────────────────────────────────────────
function MonthCard({ orders, userCount }) {
  const now = new Date();
  const start = startOf("month");
  const filtered = filterByPeriod(orders, "month");
  return (
    <div style={{ ...S.card, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={S.periodLabel}>This month</div>
          <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{fmtDate(start)} – {fmtDate(now)}</div>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 36, fontWeight: 500, color: "#111827", lineHeight: 1 }}>{filtered.length}</span>
          <span style={{ fontSize: 16, color: "#9CA3AF" }}>orders</span>
        </div>
      </div>


      <div style={S.divider} />

      <div>
        <p style={{ ...S.sectionLabel, marginBottom: 8 }}>By category</p>
        <CatGrid counts={countByCat(filtered)} />
      </div>

      <div style={S.divider} />

      <div style={S.userBox}>
        <span style={{ fontSize: 11, color: "#6B7280" }}>New registrations</span>
        <span style={{ fontSize: 18, fontWeight: 500, color: "#27500A" }}>{userCount}</span>
      </div>
    </div>
  );
}

// ── THIS WEEK card (hero) ─────────────────────────────────────────────────────
function WeekCard({ orders, userCount }) {
  const now = new Date();
  const weekStart = startOf("week");
  const filtered = filterByPeriod(orders, "week");
  return (
    <div style={S.cardWeek}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={S.periodLabel}>This week</div>
          <div style={{ fontSize: 10, color: "#185FA5", marginTop: 2 }}>
            {fmtDate(weekStart)} – {fmtDate(now)}
          </div>
        </div>
        <Badge bg="#EAF3DE" color="#27500A">7 days</Badge>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontSize: 46, fontWeight: 500, color: "#185FA5", lineHeight: 1 }}>{filtered.length}</span>
        <span style={{ fontSize: 12, color: "#9CA3AF" }}>total orders</span>
      </div>

      <div style={S.divider} />

      <CatListWeek counts={countByCat(filtered)} lastDates={lastDateByCat(filtered)} />

      <div style={S.divider} />

      <div style={{ ...S.userBox, padding: "13px 14px" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 2 }}>New registrations this week</div>
          <div style={{ fontSize: 10, color: "#9CA3AF" }}>{fmtDate(weekStart)} – {fmtDate(now)}</div>
        </div>
        <span style={{ fontSize: 26, fontWeight: 500, color: "#27500A", flexShrink: 0 }}>{userCount}</span>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
class C16AllOrdersDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ordersData: [],
      usersData: [],
      loading: true,
      error: null,
      containerWidth: null,
    };
    this.containerRef = React.createRef();
    this.ro = null;
  }

  componentDidMount() {
    this.fetchData();
    this.ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        this.setState({ containerWidth: entry.contentRect.width });
      }
    });
    if (this.containerRef.current) this.ro.observe(this.containerRef.current);
  }

  componentWillUnmount() {
    if (this.ro) this.ro.disconnect();
  }

  fetchData = async () => {
    this.setState({ loading: true, error: null });
    try {
      const [ordersRes, usersRes] = await Promise.all([
        HttpRequest(
          "GET",
          `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C16SalesOrdersDashboardView`,
          null,
          "authorizeJson"
        ),
        HttpRequest(
          "GET",
          `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C16SalesOrdersDashboardRegisterationsView`,
          null,
          "authorizeJson"
        ),
      ]);
      this.setState({
        ordersData: ordersRes.isSuccess ? (ordersRes.data || []) : [],
        usersData: usersRes.isSuccess ? (usersRes.data || []) : [],
        loading: false,
      });
    } catch (err) {
      this.setState({ loading: false, error: "Failed to load data. Please try again." });
    }
  };

  getUserCount(period) {
    return filterUsersByPeriod(this.state.usersData, period).length;
  }

  render() {
    const { ordersData, loading, error, containerWidth } = this.state;
    const now = new Date();
    const width = containerWidth ?? (this.containerRef.current ? this.containerRef.current.offsetWidth : window.innerWidth);
    const isWide = width >= 680;

    if (loading) {
      return (
        <div style={{ padding: 40, textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
          Loading overview...
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: 40, textAlign: "center", color: "#A32D2D", fontSize: 13 }}>
          {error}
          <div style={{ marginTop: 12 }}>
            <button
              onClick={this.fetchData}
              style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={this.containerRef}
        style={{
          fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          padding: 16,
          background: "#F8F9FC",
          minHeight: "100vh",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Page header */}
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, color: "#111827", margin: 0 }}>Orders Overview</h2>
          <p style={{ fontSize: 12, color: "#9CA3AF", margin: "4px 0 0" }}>
            As of {now.toLocaleDateString("en-PK", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>

        {isWide ? (
          /*
           * Wide layout:
           * Left  (62–65%) → Day card on top + Month card below, both stretch to fill
           * Right (35–38%) → Week card stretches to match the full left height
           * alignItems: "stretch" makes both columns the same height
           * The left column uses flexGrow on MonthCard so Day+Month fill the height
           */
          <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)",
            gap: 12,
            alignItems: "stretch",
            width: "100%",
          }}>
            {/* Left column — Day on top, Month fills remaining space */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              minWidth: 0,
            }}>
              <DayCard orders={ordersData} userCount={this.getUserCount("day")} />
              <MonthCard orders={ordersData} userCount={this.getUserCount("month")} />
            </div>

            {/* Right column — Week card stretches to full left-column height */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}>
              <WeekCard orders={ordersData} userCount={this.getUserCount("week")} />
            </div>
          </div>
        ) : (
          /* Narrow: Week on top, then Day, then Month stacked */
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
            <WeekCard orders={ordersData} userCount={this.getUserCount("week")} />
            <DayCard orders={ordersData} userCount={this.getUserCount("day")} />
            <MonthCard orders={ordersData} userCount={this.getUserCount("month")} />
          </div>
        )}
      </div>
    );
  }
}

export default C16AllOrdersDashboard;
